import { IPlace } from "../models/Models";
import { Change, ChangeSet, ChangeSetListener, createChangeSet } from "../DataStore/ChangeSet";
import { DataProvider, DataProviderArgs } from "../DataStore/DataProvider";
import { ReferenceCountedMap } from "../ReferenceCountedMap";
import fluent, { FluentIterable } from "fluent-iterable";

export type Country = {
    id: string;
    states: string[];
};

class ReferenceCountedCountry {
    public readonly states = new ReferenceCountedMap<string>();
    constructor(public readonly name: string) {}
}

/**
 * CountryProvider provides access to all countries and states that are referenced by
 * the current place table.
 */
export class CountryProvider extends DataProvider implements ChangeSetListener {
    private _countriesMap = new ReferenceCountedMap<ReferenceCountedCountry>("name");
    private _countries: Country[] = [];

    constructor(args: DataProviderArgs) {
        super(args, [{ source: "places", paths: ["data.address.country", "data.address.state"] }]);
    }

    override onChangeSet(_source: DataProvider, changeSet: ChangeSet<any>): void {
        if (changeSet.hasChanges()) {
            const oldState = [...this._countries];

            changeSet.created.forEach((x) => {
                this._addPlace(x);
            });

            changeSet.deleted.forEach((x) => {
                this._deletePlace(x);
            });

            changeSet.updated.forEach((change: Change<IPlace>) => {
                this._deletePlace(change.oldValue);
                this._addPlace(change.newValue);
            });

            this._countries = this._toArray();
            if (this._hasListeners()) {
                const localChanges = createChangeSet(oldState, this._countries);
                this._notifyListeners(localChanges);
            }
        }
    }

    public override async all(): Promise<FluentIterable<Country>> {
        return fluent(this._countries);
    }

    private _toArray(): Country[] {
        const a: Country[] = [];

        for (const c of this._countriesMap) {
            const s: string[] = [];
            for (const state of c.value.states) {
                s.push(state.value);
            }
            a.push({ id: c.value.name, states: s });
        }

        return a;
    }

    private _addPlace(place: IPlace): void {
        // eslint-disable-next-line prefer-const
        let { country, name: countryName } = this._getCountyFromPlace(place);
        if (!country) {
            country = new ReferenceCountedCountry(countryName);
            this._countriesMap.add(country);
        }

        const stateName = place.data.address?.state;
        if (stateName) {
            country.states.add(stateName);
        }
    }

    private _deletePlace(place: IPlace): void {
        // eslint-disable-next-line prefer-const
        let { country, name: countryName } = this._getCountyFromPlace(place);
        if (country && countryName) {
            const stateName = place.data.address?.state;
            if (stateName) {
                country.states.delete(stateName);
            }
            this._countriesMap.delete(countryName);
        }
    }

    private _getCountyFromPlace(place: IPlace): {
        country: ReferenceCountedCountry | undefined;
        name: string;
    } {
        if (place.data.address && place.data.address.country) {
            const name = place.data.address.country;
            const country = this._countriesMap.get(name);
            if (country) {
                return {
                    country,
                    name,
                };
            }
        }
        return { country: undefined, name: "" };
    }
}
